/**
 * Expédition A1 — mapping GET Prisma → formulaire Admin → PUT settings.
 * Whitelist stricte : aucune métadonnée serveur (id, timestamps, relations).
 * A2.0 — baseShippingFeeTtc (frais Expédition TTC fixe client).
 */

export const EXPEDITION_SETTINGS_FORM_FIELD_KEYS = Object.freeze([
  'enabled',
  'senderFirstName',
  'senderLastName',
  'senderAddressType',
  'senderName',
  'senderCompany',
  'senderStreet',
  'senderPostalCode',
  'senderCity',
  'senderCountry',
  'senderPhone',
  'senderEmail',
  'returnSameAsSender',
  'returnName',
  'returnCompany',
  'returnStreet',
  'returnPostalCode',
  'returnCity',
  'returnCountry',
  'preparationDelayDays',
  'baseShippingFeeTtc',
  'freeShippingThresholdTtc',
]);

export const EXPEDITION_SETTINGS_PUT_FIELD_KEYS = Object.freeze([
  'commercantId',
  ...EXPEDITION_SETTINGS_FORM_FIELD_KEYS,
]);

export const EXPEDITION_SETTINGS_SERVER_READONLY_KEYS = Object.freeze([
  'id',
  'commercantId',
  'createdAt',
  'updatedAt',
  'commercant',
]);

export const EMPTY_EXPEDITION_SETTINGS_FORM = Object.freeze({
  enabled: false,
  senderFirstName: '',
  senderLastName: '',
  senderAddressType: 'BUSINESS',
  senderName: '',
  senderCompany: '',
  senderStreet: '',
  senderPostalCode: '',
  senderCity: '',
  senderCountry: 'FR',
  senderPhone: '',
  senderEmail: '',
  returnSameAsSender: true,
  returnName: '',
  returnCompany: '',
  returnStreet: '',
  returnPostalCode: '',
  returnCity: '',
  returnCountry: 'FR',
  preparationDelayDays: 1,
  baseShippingFeeTtc: '',
  freeShippingThresholdTtc: '',
});

/**
 * @param {unknown} value
 * @param {string} [fallback='']
 */
function toFormString(value, fallback = '') {
  if (value == null) return fallback;
  return String(value);
}

/**
 * @param {unknown} value
 */
function toPreparationDelayDays(value) {
  if (value == null || value === '') return EMPTY_EXPEDITION_SETTINGS_FORM.preparationDelayDays;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : EMPTY_EXPEDITION_SETTINGS_FORM.preparationDelayDays;
}

/**
 * GET Prisma (ou réponse PUT) → state formulaire Admin uniquement.
 * @param {Record<string, unknown>} [raw]
 */
export function normalizeExpeditionSettingsForForm(raw = {}) {
  const source = raw && typeof raw === 'object' ? raw : {};
  return {
    enabled: Boolean(source.enabled),
    senderFirstName: toFormString(source.senderFirstName),
    senderLastName: toFormString(source.senderLastName),
    senderAddressType: toFormString(source.senderAddressType, 'BUSINESS'),
    senderName: toFormString(source.senderName),
    senderCompany: toFormString(source.senderCompany),
    senderStreet: toFormString(source.senderStreet),
    senderPostalCode: toFormString(source.senderPostalCode),
    senderCity: toFormString(source.senderCity),
    senderCountry: toFormString(source.senderCountry, 'FR'),
    senderPhone: toFormString(source.senderPhone),
    senderEmail: toFormString(source.senderEmail),
    returnSameAsSender: source.returnSameAsSender !== false,
    returnName: toFormString(source.returnName),
    returnCompany: toFormString(source.returnCompany),
    returnStreet: toFormString(source.returnStreet),
    returnPostalCode: toFormString(source.returnPostalCode),
    returnCity: toFormString(source.returnCity),
    returnCountry: toFormString(source.returnCountry, 'FR'),
    preparationDelayDays: toPreparationDelayDays(source.preparationDelayDays),
    baseShippingFeeTtc:
      source.baseShippingFeeTtc == null ? '' : String(source.baseShippingFeeTtc),
    freeShippingThresholdTtc:
      source.freeShippingThresholdTtc == null ? '' : String(source.freeShippingThresholdTtc),
  };
}

/**
 * @param {Record<string, unknown>} payload
 */
export function assertExpeditionSettingsUpdatePayloadSafe(payload) {
  const keys = Object.keys(payload || {});
  for (const key of EXPEDITION_SETTINGS_SERVER_READONLY_KEYS) {
    if (key === 'commercantId') continue;
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      throw new Error(`Champ serveur interdit dans le payload PUT settings: ${key}`);
    }
  }
  for (const key of keys) {
    if (!EXPEDITION_SETTINGS_PUT_FIELD_KEYS.includes(key)) {
      throw new Error(`Champ non autorisé dans le payload PUT settings: ${key}`);
    }
  }
}

/**
 * State formulaire → payload PUT whitelisté.
 * @param {Record<string, unknown>} settings
 * @param {string} commercantId
 */
export function buildExpeditionSettingsUpdatePayload(settings, commercantId) {
  const form = normalizeExpeditionSettingsForForm(settings);
  /** @type {Record<string, unknown>} */
  const payload = {
    commercantId: String(commercantId || ''),
    enabled: form.enabled,
    senderFirstName: form.senderFirstName,
    senderLastName: form.senderLastName,
    senderAddressType: form.senderAddressType || null,
    senderName: form.senderName,
    senderCompany: form.senderCompany,
    senderStreet: form.senderStreet,
    senderPostalCode: form.senderPostalCode,
    senderCity: form.senderCity,
    senderCountry: form.senderCountry,
    senderPhone: form.senderPhone,
    senderEmail: form.senderEmail,
    returnSameAsSender: form.returnSameAsSender,
    returnName: form.returnName,
    returnCompany: form.returnCompany,
    returnStreet: form.returnStreet,
    returnPostalCode: form.returnPostalCode,
    returnCity: form.returnCity,
    returnCountry: form.returnCountry,
    preparationDelayDays: form.preparationDelayDays,
    baseShippingFeeTtc:
      form.baseShippingFeeTtc === '' ? null : Number(form.baseShippingFeeTtc),
    freeShippingThresholdTtc:
      form.freeShippingThresholdTtc === '' ? null : Number(form.freeShippingThresholdTtc),
  };
  assertExpeditionSettingsUpdatePayloadSafe(payload);
  return payload;
}
